import React, { useState } from "react";
import DataPieChart from "../dataPieChart/DataPieChart";
import TimeSegmentButtons from "../timeSegmentButtons/TimeSegmentButtons";
import { useQuery } from "@tanstack/react-query";
import { fetchUsersByCountry } from "../../services/adminService";
import { useSelector } from "react-redux";

const UsersCountries = () => {
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("today");
  const userId = useSelector((state) => state.auth.id);

  const {
    data: userByCountryData,
    isLoading: usersByCountryLoading,
    error: usersByCountryError,
  } = useQuery({
    queryKey: ["usersByCountry"],
    queryFn: fetchUsersByCountry,
    enabled: !!userId,
  });

  // Helper function to get users based on selected time filter
  const getUserCount = (country, timeFilter) => {
    if (!userByCountryData || !userByCountryData[country]) return 0;

    switch (timeFilter) {
      case "today":
        return userByCountryData[country]?.today || 0;
      case "thisWeek":
        return userByCountryData[country]?.thisWeek || 0;
      case "lastWeek":
        return userByCountryData[country]?.lastWeek || 0;
      default:
        return userByCountryData[country]?.total || 0;
    }
  };

  // Get users for each country based on selected time filter
  const usersInUSA = getUserCount("USA", selectedTimeFilter);
  const usersInCA = getUserCount("Canada", selectedTimeFilter);
  const usersInOther = getUserCount("Other", selectedTimeFilter);

  const countryColors = {
    Canada: "#FF1493",
    USA: "#4B0082",
    Other: "#E6E6FA",
  };

  const predefinedValues = {
    Canada: usersInCA,
    USA: usersInUSA,
    Other: usersInOther,
  };

  const chartData = userByCountryData
    ? Object.keys(predefinedValues).map((name) => ({
        name,
        value: predefinedValues[name] || 0,
        color: countryColors[name] || "#000000",
      }))
    : [];

  return (
    <div className="p-5 bg-white shadow-lg rounded-[4px] min-h-full flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <p className="font-semibold text-base">Users From Countries</p>
      </div>
      <TimeSegmentButtons
        onSegmentChange={setSelectedTimeFilter}
        activeSegment={selectedTimeFilter}
      />
      {usersByCountryLoading ? (
        <div className="flex items-center justify-center h-[300px]">
          <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500 border-solid"></div>
        </div>
      ) : usersByCountryError ? (
        <div className="flex flex-col items-center justify-center h-[300px] bg-red-100 text-red-600 p-4 rounded-lg">
          <p className="font-semibold text-lg">Something went wrong!</p>
          <p className="text-sm">Please try again later.</p>
        </div>
      ) : chartData.every((item) => item.value === 0) ? (
        <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
          <p className="font-semibold text-lg">No data available</p>
          <p className="text-sm">Try selecting a different time range.</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4">
          <DataPieChart
            data={chartData}
            width={300}
            height={300}
            outerRadius={120}
          />
        </div>
      )}
    </div>
  );
};

export default UsersCountries;
